import React, { useEffect, useMemo, useState } from "react";
import { baseApiClient } from "data/modules/api/api.client";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { useCustomInfiniteQuery } from "features/Common/modules/Api/hooks/useCustomInfiniteQuery";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { checkIsObjectEmpty } from "features/Common/modules/DataTypes/utils/object";
import { CHANGE_STATES } from "features/Common/modules/Form/constants/Form.constants";
import { is_empty } from "features/Common/utils/common.utils";
import {
  MEMBERSHIP_API_PATHNAME,
  MEMBERSHIP_APIS,
} from "features/ManageOfferings/modules/MembershipsSection/constants/Membership.apis";
import {
  CHANNEL_API_KEYS,
  MEMBERSHIP_TIER_DETAILS_KEYS,
} from "features/ManageOfferings/modules/MembershipsSection/constants/MembershipsSection.data";
import { useMembershipStore } from "features/ManageOfferings/modules/MembershipsSection/store/useMembershipStore";
import { CHANNEL_ACCESS_TYPES } from "ui/pages/ManageBrandedCommunity/ManageCommunityHome/components/Sidenav/components/AddChannelModal/pages/ChannelDetails/ChannelDetails.data";
import { ListboxComponent } from "../../modules/ListBoxComponent/ListBoxComponent";
import { DEFAULT_PAGE_SIZE } from "@my-scoot/exly-react-component-lib";

export const useChannelDropdown = () => {
  const listingId = useMembershipStore((state) => state.listingId);
  const membershipTierDetail = useMembershipStore(
    (state) => state.membershipTierDetails
  );
  const setMembershipTierDetails = useMembershipStore(
    (state) => state.setMembershipTierDetails
  );
  const selectedRecord = useMembershipStore((state) => state.selectedRecord);
  const [selected, setSelected] = useState([]);

  const scrollRef = React.useRef(0);
  const listboxRef = React.useRef(null);

  const fetchChannelList = async ({ pageParam = 0 }) => {
    const page = pageParam + 1;
    if (listingId) {
      const channelListResponse = await baseApiClient.get(
        MEMBERSHIP_API_PATHNAME.getChannelList({
          listingUuid: listingId,
          page: page,
          pageSize: DEFAULT_PAGE_SIZE,
        })
      );

      return {
        data: channelListResponse?.data?.data?.community_membership_list,
        page,
        totalPages: channelListResponse?.data?.data?.total_pages,
        total: channelListResponse?.data?.data?.total,
      };
    }
  };

  const {
    data: communityChannelList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCustomInfiniteQuery({
    queryKey: [MEMBERSHIP_APIS.getChannelList, listingId],
    queryFn: fetchChannelList,
    getNextPageParam: (lastPage) =>
      lastPage?.page < lastPage?.totalPages ? lastPage.page + 1 : undefined,
    enabled: !!listingId,
  });

  const { data: membershipChannelList } = useCustomQuery({
    queryKey: [MEMBERSHIP_APIS.getChannelList, listingId, selectedRecord],
    url: MEMBERSHIP_API_PATHNAME.getChannelListByMembershipId(
      listingId,
      selectedRecord?.uuid
    ),
    enabled: !!selectedRecord,
  });

  const sanitizeArray = (merged) => {
    return Array.from(
      new Map(
        merged.map((item) => [item?.[CHANNEL_API_KEYS.CHANNEL_ID], item])
      ).values()
    );
  };

  const channelOptionList = useMemo(() => {
    if (!communityChannelList || communityChannelList.pages.includes(undefined))
      return [];

    // flatten channel entries from all fetched pagination pages into a single array
    const merged =
      communityChannelList.pages.flatMap((page) => page.data) ?? [];

    // remove duplicate channels caused by pagination overlap using channelId as the unique key
    return sanitizeArray(merged);
  }, [communityChannelList]);

  const StableListbox = useMemo(() => {
    return React.forwardRef(function StableListbox(props, ref) {
      return (
        <ListboxComponent
          {...props}
          ref={ref}
          scrollRef={scrollRef}
          listboxRef={listboxRef}
          onLoadMore={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      );
    });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  React.useLayoutEffect(() => {
    if (listboxRef.current) {
      listboxRef.current.scrollTop = scrollRef.current;
    }
  });

  const selectedOptions = useMemo(() => {
    return channelOptionList.filter((opt) =>
      selected?.some(
        (sel) =>
          sel?.[CHANNEL_API_KEYS.CHANNEL_ID] ===
          opt?.[CHANNEL_API_KEYS.CHANNEL_ID]
      )
    );
  }, [channelOptionList, selected]);

  const lockedIds = useMemo(() => {
    return channelOptionList
      .filter((opt) => opt?.access_type === CHANNEL_ACCESS_TYPES.PUBLIC)
      .map((opt) => opt?.[CHANNEL_API_KEYS.CHANNEL_ID]);
  }, [channelOptionList]);

  useEffect(() => {
    // the purpose is to add the open channels as selected channel in the channel dropdown
    if (checkIsArrayEmpty(channelOptionList)) return;

    // auto-select publicly accessible channels and mark them as locked (non-removable in UI)
    const locked = channelOptionList
      .filter((opt) => opt.access_type === CHANNEL_ACCESS_TYPES.PUBLIC)
      .map((opt) => ({
        channel_uuid: opt?.[CHANNEL_API_KEYS.CHANNEL_ID],
        channel_type: opt?.[CHANNEL_API_KEYS.CHANNEL_TYPE],
      }));

    setSelected(() => {
      // merge locked channels with existing selections while preventing duplicates
      return sanitizeArray([...locked]);
    });

    // sync auto-selected locked channels into membership payload with "added" change state
    setMembershipTierDetails(
      MEMBERSHIP_TIER_DETAILS_KEYS.CHANNELS,
      sanitizeArray([...locked]).map((selectedValue) => {
        return {
          ...selectedValue,
          change_type: CHANGE_STATES.added,
        };
      })
    );
  }, [channelOptionList]);

  useEffect(() => {
    // this is to fetching the channels when the editting the membership
    if (
      membershipChannelList &&
      !checkIsObjectEmpty(membershipChannelList) &&
      membershipChannelList?.community_membership_list &&
      !checkIsArrayEmpty(membershipChannelList?.community_membership_list) &&
      selectedRecord
    ) {
      const preSelectedChannels = [];
      // filtering the channels that are there in the membership based on the flag "is_mapped"
      membershipChannelList.community_membership_list.forEach(
        (selectedChannel) => {
          if (selectedChannel?.[CHANNEL_API_KEYS.IS_MEMBERSHIP_MAPPED]) {
            preSelectedChannels.push({
              [CHANNEL_API_KEYS.CHANNEL_ID]:
                selectedChannel?.[CHANNEL_API_KEYS.CHANNEL_ID],
              [CHANNEL_API_KEYS.CHANNEL_TYPE]:
                selectedChannel?.[CHANNEL_API_KEYS.CHANNEL_TYPE],
            });
          }
        }
      );
      // setting the value to the channel dropdown
      setSelected(() => {
        return sanitizeArray([...preSelectedChannels]);
      });

      // syncing channels into membership payload
      const membershipPreSelectedChannelList = sanitizeArray([
        ...preSelectedChannels,
      ]).map((selectedValue) => {
        return {
          ...selectedValue,
          change_type: CHANGE_STATES.added,
        };
      });
      setMembershipTierDetails(
        MEMBERSHIP_TIER_DETAILS_KEYS.CHANNELS,
        membershipPreSelectedChannelList
      );
    }
  }, [membershipChannelList]);

  const handleChannelChange = ({ newValue }) => {
    const formatted = newValue
      .filter(
        (item) =>
          !lockedIds.includes(item?.[CHANNEL_API_KEYS.CHANNEL_ID]) ||
          item.access_type !== CHANNEL_ACCESS_TYPES.PUBLIC
      )
      .map((item) => ({
        channel_uuid: item?.[CHANNEL_API_KEYS.CHANNEL_ID],
        channel_type: item?.[CHANNEL_API_KEYS.CHANNEL_TYPE],
      }));

    const lockedFormatted = channelOptionList
      .filter((opt) => opt.access_type === CHANNEL_ACCESS_TYPES.PUBLIC)
      .map((opt) => ({
        channel_uuid: opt?.[CHANNEL_API_KEYS.CHANNEL_ID],
        channel_type: opt?.[CHANNEL_API_KEYS.CHANNEL_TYPE],
      }));

    const finalSelectedValues = [
      ...lockedFormatted,
      ...formatted.filter(
        (item) => !lockedIds.includes(item?.[CHANNEL_API_KEYS.CHANNEL_ID])
      ),
    ];
    setSelected([...finalSelectedValues]);

    setMembershipTierDetails(
      MEMBERSHIP_TIER_DETAILS_KEYS.CHANNELS,
      finalSelectedValues.map((selectedValue) => {
        return {
          ...selectedValue,
          change_type: CHANGE_STATES.added,
        };
      })
    );
  };

  const getChannelList = ({ status = undefined, isShowList = false } = {}) => {
    if (
      membershipChannelList &&
      !is_empty(membershipChannelList) &&
      membershipChannelList?.community_membership_list &&
      !checkIsArrayEmpty(membershipChannelList?.community_membership_list)
    ) {
      const selectedChannelFilter = [];
      membershipChannelList.community_membership_list.forEach((channel) => {
        if (channel?.[CHANNEL_API_KEYS.IS_MEMBERSHIP_MAPPED]) {
          let channelObject = {
            [CHANNEL_API_KEYS.CHANNEL_ID]:
              channel?.[CHANNEL_API_KEYS.CHANNEL_ID],
            [CHANNEL_API_KEYS.CHANNEL_TYPE]:
              channel?.[CHANNEL_API_KEYS.CHANNEL_TYPE],
            change_type: status,
          };
          if (isShowList) {
            channelObject.title = channel?.channel_name;
            channelObject.icon = channel?.channel_icon;
          }
          selectedChannelFilter.push(channelObject);
        }
      });
      return selectedChannelFilter;
    }
  };

  const extractEditedChannels = () => {
    const exisitngChannelMap = new Map(
      (getChannelList() || []).map((channel) => [
        channel?.[CHANNEL_API_KEYS.CHANNEL_ID],
        channel,
      ])
    );
    const updatedChannelMap = new Map(
      (membershipTierDetail?.[MEMBERSHIP_TIER_DETAILS_KEYS.CHANNELS] || []).map(
        (channel) => [channel?.[CHANNEL_API_KEYS.CHANNEL_ID], channel]
      )
    );

    const result = [];

    // Deleted → exists in exisitngChannel but not in updatedChannel
    for (const [channel_uuid, channel] of exisitngChannelMap) {
      if (!updatedChannelMap.has(channel_uuid)) {
        result.push({
          [CHANNEL_API_KEYS.CHANNEL_ID]: channel?.[CHANNEL_API_KEYS.CHANNEL_ID],
          [CHANNEL_API_KEYS.CHANNEL_TYPE]:
            channel?.[CHANNEL_API_KEYS.CHANNEL_TYPE],
          change_type: CHANGE_STATES.deleted,
        });
      }
    }

    // Added → exists in updatedChannel but not in exisitngChannel
    for (const [channel_uuid, channel] of updatedChannelMap) {
      if (!exisitngChannelMap.has(channel_uuid)) {
        result.push({
          [CHANNEL_API_KEYS.CHANNEL_ID]: channel?.[CHANNEL_API_KEYS.CHANNEL_ID],
          [CHANNEL_API_KEYS.CHANNEL_TYPE]:
            channel?.[CHANNEL_API_KEYS.CHANNEL_TYPE],
          change_type: CHANGE_STATES.added,
        });
      }
    }

    return result;
  };

  return {
    membershipChannelList,
    communityChannelList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    channelOptionList,
    selectedOptions,
    lockedIds,
    selected,
    setSelected,
    handleChannelChange,
    getChannelList,
    extractEditedChannels,
    StableListbox,
  };
};
